import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import {
  Columns, Languages, Database, BookOpen, Settings2,
  ShieldCheck, FolderUp, Users, ClipboardList, Award
} from 'lucide-react';

const FEATURES = [
  { icon: Columns, title: 'CAT Workspace', tag: 'Professional', color: '#2563eb', bg: 'rgba(37,99,235,0.1)', grad: 'linear-gradient(135deg,#1e3a8a 0%,#1d4ed8 100%)', desc: "Segment-by-segment editor with source/target view. No guesswork, no lost formatting.", pills: ['Side-by-side editor', 'Segment progress', 'Auto-save'], hi: ['Upload DOCX, XLSX, PPTX files', 'Translation Memory & Glossary integration', 'Export translated file with formatting intact'] },
  { icon: Languages, title: 'Translation & MTPE', tag: 'All Plans', color: '#059669', bg: 'rgba(5,150,105,0.1)', grad: 'linear-gradient(135deg,#065f46 0%,#059669 100%)', desc: "Upload your document, get a translated file with all formatting preserved on export.", pills: ['DOCX PDF XLSX PPTX', 'Format preserved', 'Dual-engine quality'], hi: ['Supports all major Indian languages', 'Post-editing with quality scoring', 'Batch file processing available'] },
  { icon: Database, title: 'Translation Memory', tag: 'Starter+', color: '#0284c7', bg: 'rgba(2,132,199,0.1)', grad: 'linear-gradient(135deg,#0c4a6e 0%,#0284c7 100%)', desc: "Every approved segment stored and reused. Repeated content translates instantly.", pills: ['Auto-suggest', 'TM isolation', 'Import/export TMX'], hi: ['Auto-suggest on repeated segments', 'Project-level TM isolation', 'Import/export in TMX format'] },
  { icon: BookOpen, title: 'Glossary', tag: 'Starter+', color: '#d97706', bg: 'rgba(217,119,6,0.1)', grad: 'linear-gradient(135deg,#78350f 0%,#d97706 100%)', desc: "Build a term base. Every translation enforces your approved terminology automatically.", pills: ['Upload CSV', 'Domain terms', 'Auto-enforcement'], hi: ['Upload CSV glossaries instantly', 'Domain-specific term control', 'Automatic enforcement per segment'] },
  { icon: Settings2, title: 'Context Injection', tag: 'All Plans', color: '#dc2626', bg: 'rgba(220,38,38,0.1)', grad: 'linear-gradient(135deg,#7f1d1d 0%,#dc2626 100%)', desc: "Set tone and style once — injected into every translation request automatically.", pills: ['Tone control', 'Client context', 'Global scope'], hi: ['Set project-level tone and register', 'Client-specific terminology notes', 'Apply globally or per project'] },
  { icon: ShieldCheck, title: 'TQA Reports', tag: 'Professional+', color: '#7c3aed', bg: 'rgba(124,58,237,0.1)', grad: 'linear-gradient(135deg,#4c1d95 0%,#7c3aed 100%)', desc: "Automated quality checks catch errors and glossary violations before delivery.", pills: ['Glossary check', 'Error reports', 'TM scoring'], hi: ['Glossary compliance check per segment', 'Automated error detection', 'Detailed QA report on export'] },
  { icon: FolderUp, title: 'Batch Processing', tag: 'Starter+', color: '#0d9488', bg: 'rgba(13,148,136,0.1)', grad: 'linear-gradient(135deg,#134e4a 0%,#0d9488 100%)', desc: "Translate multiple files in one job. Mixed types, same settings, bulk export.", pills: ['Mixed batches', 'Bulk export', 'Per-file tracking'], hi: ['Upload multiple files at once', 'Mixed file types in same batch', 'Track progress per file'] },
  { icon: Users, title: 'Team Collaboration', tag: 'Business', color: '#ea580c', bg: 'rgba(234,88,12,0.1)', grad: 'linear-gradient(135deg,#7c2d12 0%,#ea580c 100%)', desc: "Invite up to 3 translators, share a 500K word pool, track individual usage.", pills: ['3 members', '500K shared', 'Usage tracking'], hi: ['Invite up to 3 team members', '500,000 shared words per month', 'Individual usage dashboard'] },
  { icon: ClipboardList, title: 'Activity Log', tag: 'All Plans', color: '#475569', bg: 'rgba(71,85,105,0.1)', grad: 'linear-gradient(135deg,#1e293b 0%,#475569 100%)', desc: "Full audit trail — translations, TM imports, plan changes. All timestamped.", pills: ['Full history', 'Filter actions', 'Export logs'], hi: ['Every action timestamped', 'Filter by type or date', 'Export full activity report'] },
  { icon: Award, title: 'Feedback Rewards', tag: 'All Plans', color: '#ca8a04', bg: 'rgba(202,138,4,0.1)', grad: 'linear-gradient(135deg,#713f12 0%,#ca8a04 100%)', desc: "Earn free word credits by rating translation outputs.", pills: ['Earn credits', 'Instant top-up', 'Community quality'], hi: ['Rate outputs to earn word credits', 'Credits top-up instantly', 'Help improve output quality'] }
];


const TESTIMONIALS = [
  { initial: 'C', color: '#2563eb', quote: "t09n has completely changed how I handle large translation projects. The TM alone saves me hours every week.", name: 'Chanchal Biswas', role: 'Hindi Translator, Uttar Pradesh' },
  { initial: 'M', color: '#7c3aed', quote: "The glossary feature is a game changer. My Punjabi translations are consistent across all documents now, no more terminology headaches.", name: 'Mohpreet Singh', role: 'Punjabi Translator, Bathinda' },
  { initial: 'G', color: '#059669', quote: "MTPE saves me at least 3 hours per project. The quality of post-editing suggestions for Marathi is remarkably accurate.", name: 'Gajanan Kare', role: 'Marathi Translator, Pune' },
  { initial: 'R', color: '#ea580c', quote: "Finally a tool built for Indian language translators. The CAT workspace with side-by-side view is exactly what I needed.", name: 'Rekha Sharma', role: 'Hindi Translator, Mumbai' },
];

const TestimonialCarousel = ({ t, current }) => {
  const idx = current % TESTIMONIALS.length;
  const tm = TESTIMONIALS[idx];
  return (
    <div style={{ background: t.bg2, borderRadius: '14px', padding: '14px 16px', transition: 'background 0.3s' }}>
      <div style={{ fontSize: '12px', color: t.text2, lineHeight: 1.6, fontStyle: 'italic', marginBottom: '10px' }}>
        "{tm.quote}"
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: tm.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span style={{ fontSize: '12px', fontWeight: 700, color: '#fff' }}>{tm.initial}</span>
        </div>
        <div>
          <div style={{ fontSize: '11px', fontWeight: 700, color: t.text1 }}>{tm.name}</div>
          <div style={{ fontSize: '10px', color: t.text3 }}>{tm.role}</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '2px' }}>
          {[1,2,3,4,5].map(s => <span key={s} style={{ color: '#f59e0b', fontSize: '11px' }}>★</span>)}
        </div>
      </div>
    </div>
  );
};

const SignupLeftPanel = () => {
  const { isDarkMode } = useTheme();
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const progressRef = useRef(null);

  const t = {
    bg: isDarkMode ? '#1c1c1e' : '#fff',
    bg2: isDarkMode ? '#2c2c2e' : '#f5f5f7',
    bg3: isDarkMode ? '#3a3a3c' : '#ebebeb',
    border: isDarkMode ? 'rgba(255,255,255,0.08)' : '#e5e5ea',
    text1: isDarkMode ? '#f5f5f7' : '#1d1d1f',
    text2: isDarkMode ? '#aeaeb2' : '#6e6e73',
    text3: isDarkMode ? '#636366' : '#aeaeb2',
    pillBg: isDarkMode ? '#2c2c2e' : '#fff',
    pillBorder: isDarkMode ? 'rgba(255,255,255,0.1)' : '#e5e5ea',
    pillText: isDarkMode ? '#ebebeb' : '#3c3c43',
    prog: isDarkMode ? '#f5f5f7' : '#1d1d1f',
  };

  const goTo = (idx) => {
    if (animating || idx === current) return;
    setAnimating(true);
    setTimeout(() => { setCurrent(idx); setAnimating(false); }, 220);
  };

  const resetTimer = () => {
    clearInterval(timerRef.current);
    clearInterval(progressRef.current);
    setProgress(0);
    let p = 0;
    progressRef.current = setInterval(() => {
      p += 100 / 40;
      setProgress(Math.min(p, 100));
      if (p >= 100) clearInterval(progressRef.current);
    }, 100);
    timerRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % FEATURES.length);
      setProgress(0);
    }, 4000);
  };

  useEffect(() => { resetTimer(); return () => { clearInterval(timerRef.current); clearInterval(progressRef.current); }; }, []);

  const f = FEATURES[current];
  const IconComp = f.icon;
  const next1 = (current + 1) % FEATURES.length;
  const next2 = (current + 2) % FEATURES.length;

  return (
    <div style={{ width: '45%', flexShrink: 0, background: t.bg, display: 'flex', flexDirection: 'column', minHeight: '100vh', borderRight: `0.5px solid ${t.border}`, transition: 'background 0.3s' }}>

      {/* Top bar */}
      <div style={{ padding: '18px 24px 16px', borderBottom: `0.5px solid ${t.border}`, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <div style={{
          width: '44px',
          height: '44px',
          background: '#000',
          borderRadius: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginBottom: '10px',
          boxShadow: isDarkMode
            ? '0 0 0 2px #ffffff'
            : '0 0 0 1px rgba(0,86,255,.30), 0 4px 20px rgba(0,86,255,.22)'
        }}>
          <img
            src="/logo-marketing.png"
            alt="t09n.com"
            style={{ width: '36px', height: '36px', objectFit: 'contain', borderRadius: 0, display: 'block' }}
          />
        </div>
        <span style={{
          fontFamily: '"All Round Gothic", AllRoundGothic, -apple-system, BlinkMacSystemFont, sans-serif',
          fontWeight: 700,
          fontStyle: 'normal',
          fontSize: '18px',
          color: isDarkMode ? '#ffffff' : '#000000',
          letterSpacing: '-0.3px',
          lineHeight: 1.1
        }}>
          t09n<span style={{ fontWeight: 700 }}>.com</span>
        </span>
        <span style={{
          fontFamily: '"All Round Gothic", AllRoundGothic, -apple-system, BlinkMacSystemFont, sans-serif',
          marginTop: '3px',
          fontSize: '11px',
          color: isDarkMode ? '#ffffff' : '#000000',
          fontWeight: 700,
          fontStyle: 'oblique',
          lineHeight: 1.2,
          letterSpacing: '0.1px'
        }}>
          The Operating System For Translators
        </span>
      </div>

      {/* Hero */}
      <div style={{ padding: '14px 24px 10px' }}>
        <p style={{ fontSize: '10px', fontWeight: 600, color: t.text3, letterSpacing: '0.06em', textTransform: 'uppercase', margin: '0 0 6px' }}>Welcome to t09n.com</p>
        <h1 style={{ fontSize: '20px', fontWeight: 700, color: t.text1, letterSpacing: '-0.03em', lineHeight: 1.15, margin: '0 0 6px' }}>Built for the<br />professional<br />translator.</h1>
        <p style={{ fontSize: '12px', color: t.text2, margin: 0, lineHeight: 1.6 }}>Every tool you need — translation, editing, QA, and team collaboration — in one workspace.</p>
      </div>

      {/* Feature card */}
      <div style={{ padding: '0 16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ background: f.grad, borderRadius: '16px', padding: '18px', opacity: animating ? 0 : 1, transform: animating ? 'translateY(4px)' : 'translateY(0)', transition: 'opacity 0.22s, transform 0.22s', marginBottom: '10px', display: 'flex', flexDirection: 'column', flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px' }}>
            <div>
              <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.65)', display: 'block', marginBottom: '5px' }}>{f.tag}</span>
              <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#fff', margin: 0, letterSpacing: '-0.02em', lineHeight: 1.2 }}>{f.title}</h3>
            </div>
            <div style={{ width: '40px', height: '40px', borderRadius: '11px', background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <IconComp size={20} color="#fff" strokeWidth={2} />
            </div>
          </div>
          <p style={{ fontSize: '12px', lineHeight: 1.6, color: 'rgba(255,255,255,0.82)', margin: '0 0 12px' }}>{f.desc}</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '14px' }}>
            {f.pills.map((p, i) => (
              <span key={i} style={{ fontSize: '10px', padding: '3px 9px', borderRadius: '20px', background: 'rgba(255,255,255,0.18)', border: '0.5px solid rgba(255,255,255,0.25)', color: '#fff', fontWeight: 500 }}>{p}</span>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', marginTop: 'auto', paddingTop: '12px', borderTop: '0.5px solid rgba(255,255,255,0.15)' }}>
            {f.hi.map((h, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="8" height="6" viewBox="0 0 8 6" fill="none"><path d="M1 3L3 5L7 1" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <span style={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>{h}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Mini cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          {[FEATURES[next1], FEATURES[next2]].map((mf, i) => {
            const MIcon = mf.icon;
            return (
              <div key={i} onClick={() => { goTo(i === 0 ? next1 : next2); resetTimer(); }} style={{ background: t.bg2, borderRadius: '12px', padding: '12px', cursor: 'pointer', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = t.bg3}
                onMouseLeave={e => e.currentTarget.style.background = t.bg2}>
                <MIcon size={18} color={mf.color} strokeWidth={2} style={{ marginBottom: '5px' }} />
                <div style={{ fontSize: '8.5px', fontWeight: 700, color: mf.color, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '2px' }}>{mf.tag}</div>
                <div style={{ fontSize: '12px', fontWeight: 700, color: t.text1, letterSpacing: '-0.01em' }}>{mf.title}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Nav */}
      <div style={{ padding: '12px 24px', borderTop: `0.5px solid ${t.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {FEATURES.map((_, i) => (
              <button key={i} onClick={() => { goTo(i); resetTimer(); }} style={{ width: i === current ? '14px' : '4px', height: '4px', borderRadius: '2px', background: i === current ? t.prog : t.bg3, border: 'none', cursor: 'pointer', padding: 0, transition: 'all 0.3s ease' }} />
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '10px', color: t.text3 }}>{current + 1} of {FEATURES.length}</span>
            <button onClick={() => { goTo((current - 1 + FEATURES.length) % FEATURES.length); resetTimer(); }} style={{ width: '22px', height: '22px', borderRadius: '50%', background: t.bg2, border: 'none', color: t.text1, cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
            <button onClick={() => { goTo((current + 1) % FEATURES.length); resetTimer(); }} style={{ width: '22px', height: '22px', borderRadius: '50%', background: t.bg2, border: 'none', color: t.text1, cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
          </div>
        </div>
        <div style={{ height: '1.5px', background: t.bg3, borderRadius: '1px', overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: '1px', background: t.prog, width: `${progress}%`, transition: 'width 0.1s linear' }} />
        </div>
      </div>

      {/* Testimonial block */}
      <div style={{ padding: '0 16px', marginTop: '10px' }}>
        <TestimonialCarousel t={t} current={current} />
      </div>

      {/* Stats bar - fills blank space */}
      <div style={{ padding: '16px 24px', borderTop: `0.5px solid ${t.border}`, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
        {[
          { value: '22+', label: 'Indian Languages' },
          { value: '10+', label: 'Pro Features' },
          { value: '₹0', label: 'To Start' }
        ].map((s, i) => (
          <div key={i} style={{ textAlign: 'center', padding: '10px 8px', background: t.bg2, borderRadius: '10px' }}>
            <div style={{ fontFamily: "'AllRoundGothic', system-ui", fontWeight: 700, fontSize: '18px', color: t.text1, letterSpacing: '-0.02em', lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: '9.5px', color: t.text3, marginTop: '3px', fontWeight: 500 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Formats footer */}
      <div style={{ padding: '12px 24px', borderTop: `0.5px solid ${t.border}`, display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '9px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: t.text3 }}>Formats</span>
        {['DOCX', 'PDF', 'XLSX', 'PPTX'].map(fmt => (
          <span key={fmt} style={{ fontSize: '9px', padding: '2px 7px', borderRadius: '5px', background: t.bg2, color: t.text2, border: `0.5px solid ${t.border}`, fontWeight: 500 }}>{fmt}</span>
        ))}
      </div>
    </div>
  );
};

export default SignupLeftPanel;
